import axiosInstance from './axiosInstance';
import { API_CONFIG, getOrderUrl } from '../config/apiConfig';

export interface OrderItem {
  productId: number;
  productName: string;
  quantity: number;
  unitPrice?: number;
}

export interface CreateOrderRequest {
  userId?: number | null;
  firstname: string;
  lastname: string;
  country: string;
  streetAddress: string;
  city: string;
  district: string;
  state: string;
  pinCode: string;
  phone: string;
  email: string;
  additionalInformation?: string;
  orderItems: OrderItem[];
  orderTotal: number;
}

/**
 * Extracts a human-readable string message from an Axios error response.
 * Handles ASP.NET Core ValidationProblemDetails, error dictionaries, arrays, strings, etc.
 * Prevents "[object Object]" from ever appearing on screen.
 */
export function getApiErrorMessage(err: any): string {
  if (!err) return 'An unexpected error occurred. Please try again.';
  if (typeof err === 'string') return err;

  const data = err.response?.data;
  if (!data) return err.message || 'An unexpected error occurred. Please try again.';

  if (typeof data === 'string') {
    if (data.includes('<html') || data.includes('<!DOCTYPE')) {
      return 'Server error occurred. Please try again later.';
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data
      .map(item => (typeof item === 'string' ? item : JSON.stringify(item)))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof data === 'object') {
    // ASP.NET ModelState / ValidationProblemDetails: { errors: { Field: ["Error1", "Error2"] } }
    if (data.errors && typeof data.errors === 'object') {
      const messages: string[] = [];
      for (const key of Object.keys(data.errors)) {
        const val = data.errors[key];
        if (Array.isArray(val)) {
          messages.push(...val);
        } else if (typeof val === 'string') {
          messages.push(val);
        }
      }
      if (messages.length > 0) {
        return messages.join('. ');
      }
    }

    // Direct error map: { PinCode: ["Error"], Phone: ["Error"] }
    const values = Object.values(data);
    const flatStrings: string[] = [];
    for (const v of values) {
      if (typeof v === 'string') flatStrings.push(v);
      else if (Array.isArray(v)) {
        flatStrings.push(...v.filter(item => typeof item === 'string'));
      }
    }
    if (flatStrings.length > 0 && !data.title) {
      return flatStrings.join('. ');
    }

    if (data.message && typeof data.message === 'string') return data.message;
    if (data.title && typeof data.title === 'string') {
      if (flatStrings.length > 0) return `${data.title}: ${flatStrings.join(', ')}`;
      return data.title;
    }
    if (data.error && typeof data.error === 'string') return data.error;

    try {
      return JSON.stringify(data);
    } catch {
      return 'Failed to process request due to a server error.';
    }
  }

  return err.message || 'An unexpected error occurred.';
}

export async function createOrder(order: CreateOrderRequest) {
  const response = await axiosInstance.post<number>(
    getOrderUrl(API_CONFIG.ENDPOINTS.ORDER.CREATE),
    order
  );
  return response.data;
}

export async function getOrderDetails(orderId: number) {
  const response = await axiosInstance.post(
    getOrderUrl(API_CONFIG.ENDPOINTS.ORDER.GET_DETAILS),
    { orderId }
  );
  return response.data;
}

export async function getAllOrders(userId: number) {
  const response = await axiosInstance.post(
    getOrderUrl(API_CONFIG.ENDPOINTS.ORDER.GET_ALL),
    { userId }
  );
  return response.data as Array<{ 
    orderId: number;
    products: Array<{ productId: number; productName: string; productCategoryId: number; productCategoryName: string; productDescription: string; productImageUrl: string; productRate: number; quantity: number }>; 
    totalPrice: string;
    orderDate: string;
    userName: string;
    isComplete: boolean;
    address: string; phoneNumber: string; city: string; state: string; postalCode: string; country: string; 
  }>;
}

export async function getMyActiveOrders(userId?: number | null) {
  if (userId == null || Number(userId) === 0) {
    return [] as Array<{
      orderId: number;
      products: Array<{ productId: number; productName: string; productCategoryId: number; productCategoryName: string; productDescription: string; productImageUrl: string; productRate: number; quantity: number }>;
      totalPrice: string;
      isComplete: boolean;
      address: string; phoneNumber: string; city: string; state: string; postalCode: string; country: string;
    }>;
  }
  const response = await axiosInstance.post(
    getOrderUrl(API_CONFIG.ENDPOINTS.ORDER.GET_MY_ACTIVE),
    { userId }
  );
  return response.data as Array<{
    orderId: number;
    products: Array<{ productId: number; productName: string; productCategoryId: number; productCategoryName: string; productDescription: string; productImageUrl: string; productRate: number; quantity: number }>;
    totalPrice: string;
    orderDate: string;
    isComplete: boolean;
    address: string; phoneNumber: string; city: string; state: string; postalCode: string; country: string;
  }>;
}

export async function markOrderComplete(adminUserId: number, orderId: number) {
  const response = await axiosInstance.post(
    getOrderUrl(API_CONFIG.ENDPOINTS.ORDER.MARK_COMPLETE),
    { adminUserId, orderId }
  );
  return response.data as boolean;
}
